import React, { useState } from 'react';
import { Card, Table, Badge, Modal } from 'react-bootstrap';
import { format } from 'date-fns';

const safeFormat = (dateStr, fmt) => {
  try {
    if (!dateStr) return '--';
    return format(new Date(dateStr), fmt);
  } catch {
    return '--';
  }
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('vi-VN').format(amount || 0) + 'đ';
};

const VOUCHER_STATUS = {
  PENDING: { label: 'Chờ duyệt', variant: 'warning' },
  APPROVED: { label: 'Đã duyệt', variant: 'success' },
  REJECTED: { label: 'Từ chối', variant: 'danger' },
};

const CATEGORY_LABELS = {
  SUPPLIES: 'Vật tư',
  UTILITIES: 'Tiện ích',
  MAINTENANCE: 'Bảo trì',
  SALARY: 'Lương',
  TRANSPORTATION: 'Vận chuyển',
  MARKETING: 'Marketing',
  CASH_WITHDRAWAL: 'Rút tiền mặt',
  OTHER: 'Khác',
};

const ExpensesTab = ({ vouchers = [], voucherSummary = {} }) => {
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  // #235631: bỏ trạng thái "Đã chi" — chỉ còn đếm phiếu đã duyệt.
  const approvedCount = voucherSummary.by_status?.approved || 0;

  return (
    <>
      {/* Summary Cards */}
      <div className='row g-3 mb-4'>
        <div className='col-6 col-md-4'>
          <Card className='shadow-sm border-0 h-100'>
            <Card.Body className='d-flex align-items-center gap-3'>
              <div className='rounded bg-primary bg-opacity-10 p-2 d-flex align-items-center justify-content-center' style={{ width: 44, height: 44 }}>
                <i className='icofont-bill text-primary fs-4'></i>
              </div>
              <div>
                <div className='fw-bold fs-4'>{voucherSummary.total_vouchers || 0}</div>
                <div className='text-muted small'>Tổng phiếu chi</div>
              </div>
            </Card.Body>
          </Card>
        </div>
        <div className='col-6 col-md-4'>
          <Card className='shadow-sm border-0 h-100'>
            <Card.Body className='d-flex align-items-center gap-3'>
              <div className='rounded bg-danger bg-opacity-10 p-2 d-flex align-items-center justify-content-center' style={{ width: 44, height: 44 }}>
                <i className='icofont-money text-danger fs-4'></i>
              </div>
              <div>
                <div className='fw-bold fs-5'>{formatCurrency(voucherSummary.total_amount)}</div>
                <div className='text-muted small'>Tổng chi</div>
              </div>
            </Card.Body>
          </Card>
        </div>
        <div className='col-6 col-md-4'>
          <Card className='shadow-sm border-0 h-100'>
            <Card.Body className='d-flex align-items-center gap-3'>
              <div className='rounded bg-success bg-opacity-10 p-2 d-flex align-items-center justify-content-center' style={{ width: 44, height: 44 }}>
                <i className='icofont-check-circled text-success fs-4'></i>
              </div>
              <div>
                <div className='fw-bold fs-4'>{approvedCount}</div>
                <div className='text-muted small'>Đã duyệt</div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Voucher Table */}
      <Card className='shadow-sm border-0'>
        <Card.Body className='p-0'>
          <Table responsive hover className='mb-0 align-middle'>
            <thead className='bg-light'>
              <tr>
                <th className='border-bottom-0 ps-4' style={{ width: 50 }}>#</th>
                <th className='border-bottom-0'>Số phiếu</th>
                <th className='border-bottom-0'>Danh mục</th>
                <th className='border-bottom-0'>Mô tả</th>
                <th className='border-bottom-0'>Người nhận</th>
                <th className='border-bottom-0 text-end'>Số tiền</th>
                <th className='border-bottom-0'>Ngày chi</th>
                <th className='border-bottom-0'>Chứng từ</th>
                <th className='border-bottom-0 pe-4'>Trạng thái</th>
              </tr>
            </thead>
            <tbody>
              {vouchers.map((v, idx) => {
                const statusCfg = VOUCHER_STATUS[v.status] || { label: v.status, variant: 'secondary' };
                const isExpanded = expandedRow === v.id;

                return (
                  <React.Fragment key={v.id}>
                    <tr
                      onClick={() => setExpandedRow(isExpanded ? null : v.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className='ps-4 text-muted'>{idx + 1}</td>
                      <td className='fw-medium'>{v.voucher_number}</td>
                      <td><span className='small'>{CATEGORY_LABELS[v.category] || v.category}</span></td>
                      <td className='small text-truncate' style={{ maxWidth: 200 }}>{v.description}</td>
                      <td className='small text-muted'>{v.recipient_name || '—'}</td>
                      <td className='text-end fw-medium text-danger'>{formatCurrency(v.amount)}</td>
                      <td className='text-muted small'>{safeFormat(v.voucher_date, 'dd/MM HH:mm')}</td>
                      <td>
                        {v.evidence_urls?.length > 0 ? (
                          <div className='d-flex gap-1'>
                            {v.evidence_urls.slice(0, 2).map((url, i) => (
                              <div
                                key={i}
                                style={{ cursor: 'pointer' }}
                                onClick={(e) => { e.stopPropagation(); setLightboxUrl(url); }}
                              >
                                <img src={url} alt='' className='rounded border' style={{ width: 36, height: 28, objectFit: 'cover' }} />
                              </div>
                            ))}
                            {v.evidence_urls.length > 2 && (
                              <span className='text-muted small align-self-center'>+{v.evidence_urls.length - 2}</span>
                            )}
                          </div>
                        ) : (
                          <span className='text-muted'>—</span>
                        )}
                      </td>
                      <td className='pe-4'><Badge bg={statusCfg.variant}>{statusCfg.label}</Badge></td>
                    </tr>

                    {/* Expanded Detail Row */}
                    {isExpanded && (
                      <tr>
                        <td colSpan='9' className='bg-light px-4 py-3'>
                          <div className='row g-3'>
                            <div className='col-md-4'>
                              <div className='small text-muted mb-1'>Người tạo</div>
                              <div className='small'>{v.created_by?.full_name || '—'}</div>
                            </div>
                            <div className='col-md-4'>
                              <div className='small text-muted mb-1'>Người duyệt</div>
                              <div className='small'>{v.approved_by?.full_name || '—'}</div>
                            </div>
                            <div className='col-md-4'>
                              <div className='small text-muted mb-1'>Phương thức</div>
                              <div className='small'>{v.payment_method || '—'}</div>
                            </div>
                            {v.evidence_urls?.length > 0 && (
                              <div className='col-12'>
                                <div className='small text-muted mb-1'>Chứng từ đầy đủ</div>
                                <div className='d-flex gap-2 flex-wrap'>
                                  {v.evidence_urls.map((url, i) => (
                                    <div
                                      key={i}
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => setLightboxUrl(url)}
                                    >
                                      <img src={url} alt='' className='rounded border' style={{ width: 100, height: 75, objectFit: 'cover' }} />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {vouchers.length === 0 && (
                <tr>
                  <td colSpan='9' className='text-center py-4 text-muted'>
                    Không có phiếu chi nào trong ca này
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Lightbox Modal */}
      <Modal show={!!lightboxUrl} onHide={() => setLightboxUrl(null)} centered size='lg'>
        <Modal.Header closeButton>
          <Modal.Title className='small'>Chứng từ</Modal.Title>
        </Modal.Header>
        <Modal.Body className='text-center p-2'>
          {lightboxUrl && (
            <img
              src={lightboxUrl}
              alt='Evidence'
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ExpensesTab;
